import { HttpService } from '@ribajs/core'
import {
  couldBeJson,
  normalizeUrl,
  justDigits,
  extend,
  handleize,
} from '@ribajs/utils'
import { setPageAndSearchParameters, isColor } from '../utils/index.js'
import pRetry, { AbortError } from 'p-retry'
import { idbCache } from '@ribajs/cache'

import type {
  ShopifyProduct,
  ShopifyProductVariant,
  Metafields,
  MetafieldsProductVariant,
  ShopifyProductVariantOption,
  ShopifyProductImage,
  ShopifyImage,
  ShopifyPreviewImage,
  ShopifyMedia,
  ShopifyCartLineItemFeaturedImage,
  ShopifyNormalizedImage,
  CardProductOptionValue,
  CollectionJsonResponse,
  CardProductOption,
  SbProductBadges,
} from '../types/index.js'

export interface MetafieldsCache {
  [url: string]: Metafields
}

export class ShopifyProductService {
  protected static cache = idbCache
  protected static cacheExpiresIn = 1000 * 60 * 60 * 30 // 30 minutes

  // Used for market dependent visibility of "Neu" badge
  protected static localizedNewBadgeTags: Record<string, string | undefined> = {
    DE: 'Kategorie: Neuheiten',
    FR: 'Kategorie: Nouveautés',
    INT: 'Kategorie: New', // TODO: check if this is correct
  }

  static root = window.Shopify?.routes?.root || '/'

  private static async _getSingle(
    handle: string,
    enableCache = true,
  ): Promise<ShopifyProduct> {
    if (!handle) {
      throw new TypeError(
        '[ShopifyProductService.getSingle] The product handle is required!',
      )
    }

    const cacheKey = this.getProductCacheKey(handle)

    if (enableCache) {
      const cached =
        await this.cache.resolve<Promise<ShopifyProduct | undefined>>(cacheKey)
      if (cached) {
        return cached
      }
    }

    const res = await HttpService.getJSON<ShopifyProduct>(
      `${ShopifyProductService.root}products/${handle}.js`,
    )

    if (res.status >= 400) {
      throw new AbortError(res.statusText)
    }

    const product = res.body

    this.cache.resolve<ShopifyProduct>(cacheKey, product, this.cacheExpiresIn)

    return product
  }

  public static getProductHandleFromUrl(url: string) {
    const parts = url.split('products/')
    const handle = parts[parts.length - 1].split('?')[0]
    return handle
  }

  public static getProductCacheKey(handle: string) {
    return `product2-${handle}`
  }

  public static getMetafieldsCacheKey(productHandleOrUrl: string) {
    if (productHandleOrUrl.includes('products/')) {
      productHandleOrUrl = this.getProductHandleFromUrl(productHandleOrUrl)
    }
    return `product-metafields-${productHandleOrUrl}`
  }

  public static cacheProduct(product: ShopifyProduct) {
    const cacheKey = this.getProductCacheKey(product.handle)
    return this.cache.resolve<ShopifyProduct>(
      cacheKey,
      product,
      this.cacheExpiresIn,
    )
  }

  public static cacheMetafields(
    metafields: Metafields,
    productHandleOrUrl: string,
  ) {
    const cacheKey = this.getMetafieldsCacheKey(productHandleOrUrl)
    return this.cache.resolve<Metafields>(
      cacheKey,
      metafields,
      this.cacheExpiresIn,
    )
  }

  /**
   * Check the product and its variants and remove variants and options that are not available in the current market.
   */
  private static cleanIrrelevantVariantsAndOptions(product: ShopifyProduct) {
    product.variants = product.variants.filter(variant => {
      if (this.hiddenOnMarket(variant.metafields, window.Shopify?.country)) {
        // console.debug(
        //   `Removing variant "${variant.title}" for "${product.title}" because it is not available in ${window.Shopify?.country}`,
        // )
        return false
      }

      return true
    })

    // Remove options that are not used by any (still) existing variant
    product.options.forEach(option => {
      option.values = option.values.filter(optionValue => {
        if (
          !product.variants.some(
            variant => variant[`option${option.position}`] === optionValue,
          )
        ) {
          // console.debug(
          //   `Removing option "${optionValue}" for "${product.title}" because it is not in use`,
          // )
          return false
        }

        return true
      })
    })

    // Update available flag because we might have removed some variants
    product.available =
      product.variants.length > 0 &&
      product.variants.some(variant => variant.available)

    return product
  }

  public static async getSingle(
    handle: string,
    enableCache = true,
  ): Promise<ShopifyProduct> {
    return pRetry(this._getSingle.bind(this, handle, enableCache))
  }

  /**
   * Get products array by handles
   * @param handles The product handles
   */
  public static async get(
    handles: string | string[],
  ): Promise<ShopifyProduct[]> {
    if (typeof handles === 'string') {
      if (handles) {
        handles = [handles]
      } else {
        handles = []
      }
    }

    if (!Array.isArray(handles)) {
      throw new TypeError(
        '[ShopifyProductService.get] The product handles must be an array of product handles or a string of a single product handle!',
      )
    }

    const products: ShopifyProduct[] = []

    for await (const handle of handles) {
      const product = await this.getSingle(handle)
      products.push(product)
    }

    return products
  }

  public static getProductUrl(
    productHandle: string,
    withinCollectionHandle?: string,
    searchParameter?: URLSearchParams,
  ) {
    let _url = `/products/${productHandle}`

    if (withinCollectionHandle && !_url.includes('/collections/')) {
      _url = this.getCollectionUrl(withinCollectionHandle).pathname + _url
    }

    if (!_url?.startsWith('http')) {
      _url = window.location.protocol + '//' + window.location.host + _url
    }

    const url = new URL(_url)

    if (searchParameter) {
      searchParameter.forEach((value, key) => {
        url.searchParams.set(key, value)
      })
    }

    return url
  }

  /**
   * Get the collection url by collection url or handle, page number and other additional url search parameters
   * @param collectionHandleOrUrl
   * @param page
   * @param searchParameter
   * @returns
   */
  public static getCollectionUrl(
    collectionHandleOrUrl: string,
    page = 1,
    searchParameter?: URLSearchParams,
  ) {
    let _url = collectionHandleOrUrl.includes('/collections/')
      ? normalizeUrl(collectionHandleOrUrl).url
      : `${this.root}collections/${collectionHandleOrUrl}`

    if (!_url?.startsWith('http')) {
      _url = window.location.protocol + '//' + window.location.host + _url
    }

    const url = new URL(_url)

    return setPageAndSearchParameters(url, page, searchParameter)
  }

  private static async _collection(
    collectionHandleOrUrl: string,
    page = 1,
    searchParameter?: URLSearchParams,
  ): Promise<CollectionJsonResponse | null> {
    const url = this.getCollectionUrl(
      collectionHandleOrUrl,
      page,
      searchParameter,
    )
    // Set view=raw to get the json object, see `shopify/layout/raw.liquid` for this
    url.searchParams.set('view', 'json')

    const res = await HttpService.get<string>(url.toString())

    const template = document.createElement('template') as HTMLTemplateElement
    template.innerHTML = res.body

    const jsonStr = template.content.querySelector('section')?.innerHTML.trim()

    if (!jsonStr) {
      console.warn("Can't parse collection response!", res)
      return null
    }

    let data: any = null

    if (couldBeJson(jsonStr)) {
      try {
        data = JSON.parse(jsonStr)
      } catch (error) {
        console.error(error)
        console.error(jsonStr)
      }
    }

    return data || null
  }

  /**
   * Load a collection object and it's products by the collection handle or collection url
   * @param collectionHandleOrUrl The collection handle or url to get the collection object from
   * @param page The page number you want to get the products for
   * @param searchParameter Additional url search parameters, used for collection filtering and sorting
   * @returns
   */
  public static async collection(
    collectionHandleOrUrl: string,
    page = 1,
    searchParameter?: URLSearchParams,
  ): Promise<CollectionJsonResponse | null> {
    if (!collectionHandleOrUrl)
      throw new Error('Collection handle or url not set!')
    return pRetry(
      this._collection.bind(this, collectionHandleOrUrl, page, searchParameter),
    )
  }

  /**
   * Check if the option values fits to the current variant.
   * @param variant
   * @param optionValues
   * @return Returns `true` if the option values are fitting to the variant
   */
  public static fitsVariantOptions(
    variant: ShopifyProductVariant,
    optionValues: CardProductOptionValue[],
  ) {
    let fit = true
    // position0 is the option index starting on 0
    for (const i in optionValues) {
      if (optionValues[i]) {
        const optionValue = optionValues[i]
        fit = fit && variant.options.indexOf(optionValue.value.toString()) > -1
      }
    }
    return fit
  }

  public static getRatingTemplates(
    smallClass = 'text-base font-body',
    variantBarcode?: string,
  ) {
    let normal = ''
    let small = ''
    if (variantBarcode) {
      normal = `<etrusted-widget data-etrusted-widget-id="wdg-8238a69d-178e-41f9-ba9c-f3163a57863a" data-gtin="${variantBarcode}" class="block"></etrusted-widget>`
      small = `<etrusted-widget data-etrusted-widget-id="wdg-acb3089b-51d1-4ac0-b583-fc534656c0fa" data-gtin="${variantBarcode}" class="${smallClass}"></etrusted-widget>`
    }
    return { normal, small }
  }

  /**
   * Get product variant of (selected) option values
   * @param optionValues (selected) option values
   */
  public static getVariantOfOptions(
    product: ShopifyProduct,
    optionValues: CardProductOptionValue[],
  ) {
    let result: ShopifyProductVariant | null = null

    if (optionValues.length !== product.options.length) {
      return null
    }
    for (const i in product.variants) {
      if (product.variants[i]) {
        result = null
        const variant = product.variants[i]
        const fits = this.fitsVariantOptions(variant, optionValues)
        if (fits) {
          result = variant
          break
        }
      }
    }

    return result
  }

  /**
   * Get variant object by variant id
   * @param variantId Variant id
   */
  public static getVariant(product: ShopifyProduct, variantId: number) {
    const result =
      product.variants.find(variant => variant.id === variantId) || null
    return result
  }

  /**
   * Get product option by name
   * @param product product which holds the options
   * @param name option name
   */
  public static getOption(product: ShopifyProduct, name: string) {
    const result = product.options.find(
      option => option.name.toLowerCase() === name.toLowerCase(),
    )
    return result
  }

  /**
   * Get product option which includes a substring
   * @param product product which holds the options
   * @param name option name
   */
  public static getOptionIncludes(
    product: ShopifyProduct,
    searchSubstr: string,
  ) {
    const result = product.options.find(option =>
      option.name.includes(searchSubstr),
    )
    return result
  }

  public static normalizeImageObject(
    img:
      | string
      | ShopifyProductImage
      | ShopifyImage
      | ShopifyPreviewImage
      | ShopifyMedia
      | ShopifyCartLineItemFeaturedImage
      | null,
  ) {
    let result: Partial<ShopifyNormalizedImage> = {}

    if (typeof img === 'string') {
      result.src = img
    } else if (typeof img === 'object') {
      if (typeof (img as ShopifyProductImage)?.src === 'string') {
        result = img as ShopifyProductImage
      } else if (
        typeof (img as ShopifyMedia)?.preview_image?.src === 'string'
      ) {
        result = img as ShopifyMedia
        result.src ||= (img as ShopifyMedia)?.preview_image.src
      } else if (
        typeof (img as ShopifyCartLineItemFeaturedImage)?.url === 'string'
      ) {
        result = img as ShopifyCartLineItemFeaturedImage
        result.src ||= (img as ShopifyCartLineItemFeaturedImage).url
      } else {
        result = img as any
      }
    }

    if (!result) {
      console.warn('[normalizeImageObject] result is falsy', result)
    }

    if (typeof result?.src === 'string') {
      // remove protocol
      result.src = result.src.replace(/(^\w+:|^)\/\//, '//')
    }

    return result
  }

  public static getFeaturedImage(
    product: ShopifyProduct | null = null,
    variant: ShopifyProductVariant | null = null,
  ) {
    const image = this.normalizeImageObject(
      variant?.featured_media ||
        variant?.featured_image ||
        product?.featured_image ||
        null,
    )
    return image
  }

  /** @deprecated */
  public static setCurrentFeaturedImage(
    currentImageObject: Partial<ShopifyNormalizedImage>,
    product: ShopifyProduct | null = null,
    variant: ShopifyProductVariant | null = null,
  ) {
    const image = this.getFeaturedImage(product, variant)
    return extend({}, currentImageObject, image)
  }

  /**
   * Set the product background color image
   * @param el The element you want to set the background css variable for
   * @param bgColor background color
   * @returns
   */
  public static setImageBgColor(el: HTMLElement, bgColor?: string) {
    if (!bgColor) return

    if (!isColor(bgColor)) {
      const colorName = handleize(bgColor)
      bgColor = `var(--color-${colorName})`
      // remove all product image color classes
      for (const classItem of Array.from(el.classList)) {
        if (classItem.startsWith('product-image-bg-color-')) {
          el.classList.remove(classItem)
        }
      }
      el.classList.add(`product-image-bg-color-${colorName}`)
    }

    el.style.setProperty('--product-bg-image-color', bgColor)

    el.style.setProperty(
      '--product-bg-image',
      `linear-gradient(
      180deg,
      var(--product-bg-image-color) 75%,
      rgba(255, 255, 255, 0) 75%
    )`,
    )
  }

  public static getFirstAvailableVariant(product: ShopifyProduct) {
    const variants = product?.variants
    if (!variants || !variants.length) {
      console.error(new Error('No variants found!'))
      return
    }

    if (
      product.selected_or_first_available_variant?.available &&
      product.selected_or_first_available_variant?.hidden !== true
    ) {
      return product.selected_or_first_available_variant
    }

    if (
      product.first_available_variant?.available &&
      product.first_available_variant?.hidden !== true
    ) {
      return product.first_available_variant
    }

    // Find the first available variant
    for (const variant of variants) {
      if (variant.available && variant.hidden !== true) {
        return variant
      }
    }

    // If no variant is available, return the first variant which is not hidden
    for (const variant of variants) {
      if (variant.hidden !== true) {
        return variant
      }
    }

    return undefined
  }

  public static calcSalePercent(
    variant: ShopifyProductVariant | ShopifyProduct,
  ) {
    let sale = 0
    if (!variant) return sale

    if (variant.compare_at_price && variant.compare_at_price > variant.price)
      sale = Math.round((variant.price / variant.compare_at_price) * 100 - 100)

    return sale
  }

  public static calcSaleSum(variant: ShopifyProductVariant | ShopifyProduct) {
    let sale = 0
    if (!variant) return sale

    if (variant.compare_at_price && variant.compare_at_price > variant.price)
      sale = variant.compare_at_price - variant.price
    return sale
  }

  public static getDiscountLabel(variant?: ShopifyProductVariant) {
    return variant?.metafields?.seeberger?.discount_label || ''
  }

  public static prepareBadges(
    product: ShopifyProduct | undefined,
    variant: ShopifyProductVariant | undefined,
    metafields: Metafields | null,
  ) {
    const badges: SbProductBadges = {
      new: false,
      sale: 0,
      saleSum: 0,
      soldOut: !product?.available,
      outOfStockText: '',
      discountInfo: '',
      additionalInfo: '',
    }

    if (!product) return badges

    // Stop here if sold out
    if (badges.soldOut) return badges

    // Sale
    badges.sale = this.calcSalePercent(variant || product)

    // Sale sum
    badges.saleSum = this.calcSaleSum(variant || product)

    // Out Of Stock Text
    badges.outOfStockText = metafields?.seeberger?.out_of_stock_text || ''

    // Discount info text
    badges.discountInfo = this.getDiscountLabel(variant)

    // Additional info text
    badges.additionalInfo = metafields?.seeberger?.info_label || ''

    // New
    const newBadge = this.localizedNewBadgeTags[window.Shopify?.country || '']
    if (!newBadge) {
      console.warn(
        `Could not find new badge tag for country ${window.Shopify?.country}`,
      )
    } else {
      badges.new = product.tags.includes(newBadge)
    }

    return badges
  }

  private static async _getMetafields(
    productHandleOrUrl: string,
    enableCache = true,
  ): Promise<Metafields | null> {
    let url = productHandleOrUrl.includes('/products/')
      ? productHandleOrUrl
      : `${this.root}products/${productHandleOrUrl}`

    if (url.includes('?')) {
      url = url + '&view=json-metafields'
    } else {
      url = url + '?view=json-metafields'
    }

    const cacheKey = this.getMetafieldsCacheKey(productHandleOrUrl)

    if (enableCache) {
      const metafields = await this.cache.resolve<Promise<Metafields>>(cacheKey)
      if (metafields) {
        return metafields
      }
    }

    const res = await HttpService.get<string>(url)

    const template = document.createElement('template') as HTMLTemplateElement
    template.innerHTML = res.body

    const jsonStr = template.content.querySelector('section')?.innerHTML.trim()
    let data: any = null

    if (jsonStr && couldBeJson(jsonStr)) {
      try {
        data = JSON.parse(jsonStr)
        await this.cache.resolve<Promise<Metafields>>(
          cacheKey,
          data,
          this.cacheExpiresIn,
        )
      } catch (error) {
        console.error(error)
      }
    }

    return data || null
  }

  /**
   * Get metafields for product by handle or url
   * TODO use section rendering api? https://shopify.dev/api/section-rendering
   * @param productHandleOrUrl Product handle or url
   * @returns
   */
  public static async getMetafields(
    productHandleOrUrl: string,
    enableCache = true,
  ): Promise<Metafields | null> {
    return pRetry(
      this._getMetafields.bind(this, productHandleOrUrl, enableCache),
    )
  }

  protected static prepareProduct(product: ShopifyProduct) {
    if (product.is_prepared) {
      return product
    }

    if (!this.productHasOptionsWithValues(product)) {
      throw new Error('Product options not with values!')
    }

    // Prepare title
    product.title = product.title.replaceAll('`', "'")

    // Prepare url
    if (!product.url) {
      product.url = '/products/' + product.handle
    }

    this.cleanIrrelevantVariantsAndOptions(product)
    this.prepareOptions(product)
    product.is_prepared = true

    return product
  }

  /**
   * Get prepared product data including metafields, badges, featured image, variant, etc.
   * @param productHandleOrUrl Product handle or url
   * @param variantId Variant id
   * @param enableCache
   * @returns
   */
  public static async getPreparedProductData(
    productHandleOrUrl: string,
    variantId: number | undefined,
    enableCache = true,
    allowedVariantIds?: number[] | undefined,
  ) {
    let product = await this.getSingle(productHandleOrUrl, enableCache)

    let metafields = await this.getMetafields(productHandleOrUrl, enableCache)
    let orderQuantityLimit = -1
    let bgColor = ''
    let variant: ShopifyProductVariant | undefined = undefined

    if (metafields) {
      const {
        bgColor: bgColorFromMetafields,
        metafields: preparedMetafields,
        orderQuantityLimit: orderQuantityLimitFromMetafields,
      } = this.prepareMetafields(metafields, product)
      metafields = preparedMetafields
      orderQuantityLimit = orderQuantityLimitFromMetafields
      bgColor = bgColorFromMetafields
    }

    product = this.prepareProduct(product)

    if (allowedVariantIds?.length) {
      product.variants = product.variants.filter(variant =>
        allowedVariantIds.includes(variant.id),
      )
    }

    if (variantId) {
      variant = ShopifyProductService.getVariantById(product, variantId)
    }

    const featuredImage = this.getFeaturedImage(product, variant)

    const badges = this.prepareBadges(product, variant, metafields)

    return {
      product,
      metafields,
      badges,
      orderQuantityLimit,
      bgColor,
      featuredImage,
      variant,
    }
  }

  public static getPreparedProductVariantData(
    product: ShopifyProduct,
    variant: ShopifyProductVariant,
    metafields: Metafields | null,
  ) {
    const featuredImage = this.getFeaturedImage(product, variant)
    const badges = this.prepareBadges(product, variant, metafields)

    return {
      badges,
      featuredImage,
    }
  }

  /**
   * Get variant by id
   * @param product The product object
   * @param id The variant id
   * @returns The variant object or undefined if not found
   */
  public static getVariantById(
    product: ShopifyProduct,
    id: number | string,
  ): ShopifyProductVariant | undefined {
    id = id.toString()
    if (!product) return
    return product.variants.find(variant => variant.id.toString() === id)
  }

  /**
   * Prepare metafields for product
   * @param metafields The metafields object
   * @param product The product object
   * @returns The prepared metafields object
   */
  public static prepareMetafields(
    metafields: Metafields,
    product: ShopifyProduct,
  ) {
    const orderQuantityLimit =
      ShopifyProductService.getQuantityLimitFromMetafields(metafields)

    const bgColor = ShopifyProductService.getBgColorFromMetafields(metafields)

    // Update variant metafields
    if (metafields.variants) {
      const variantIds = Object.keys(metafields.variants)
      for (const variantId of variantIds) {
        const variant = this.getVariantById(product, variantId)
        if (!variant || !metafields.variants[variantId]) continue

        variant.metafields = metafields.variants[variantId]
      }
    }

    return {
      orderQuantityLimit,
      bgColor,
      metafields,
    }
  }

  private static prepareOptions(product: ShopifyProduct) {
    // all option names to lower case and copy to custom options array
    for (const option of product.options) {
      // Remove "Default Title" Option values
      option.values = this.renameOptionValues(option.values)

      // Sort option values, currently disabled
      // option = this.sortOption(option)
    }

    for (const variant of product.variants) {
      if (variant.options) {
        variant.options = this.renameOptionValues(variant.options)
      }
      if (variant.option1) {
        variant.option1 = this.renameOptionValue(variant.option1)
      }
      if (variant.option2) {
        variant.option2 = this.renameOptionValue(variant.option2)
      }
      if (variant.option3) {
        variant.option3 = this.renameOptionValue(variant.option3)
      }
    }

    for (const variant of product.variants) {
      if (!variant.hidden) {
        continue
      }
      // If the hidden variant has just a single option, we can just remove the option from the product
      if (variant.options.length === 1) {
        const option = variant.options[0]
        for (const o of product.options) {
          o.values = o.values.filter(
            o => o.trim().toLowerCase() !== option.trim().toLowerCase(),
          )
        }
      }
    }

    return product.options
  }

  public static hiddenOnMarket(
    variantMetafields: MetafieldsProductVariant | null | undefined,
    marketCountryCode: string | undefined,
  ) {
    if (!marketCountryCode) {
      throw new Error('marketCountryCode is undefined!')
    }
    if (!variantMetafields?.seeberger) {
      throw new Error(
        'variantMetafields.seeberger is undefined! Did you called `prepareMetafields`?',
      )
    }
    // seeberger property is set but market allowlist (`seeberger.market_whitelist`) is not set, than the variant is allowed for all markets
    if (!variantMetafields.seeberger.market_whitelist?.length) {
      return false
    }

    // Otherwise the variant is white listed for the current market
    const onAllowList =
      variantMetafields?.seeberger?.market_whitelist?.includes(
        marketCountryCode,
      ) || false

    return onAllowList === false
  }

  public static getBgColorFromMetafields(
    metafields: Metafields | null,
  ): string {
    return metafields?.seeberger?.bg_color
  }

  public static getQuantityLimitFromMetafields(
    metafields: Metafields | null,
  ): number {
    const productLimit = metafields?.seeberger?.order_quantity_limit || -1
    if (productLimit > 0) {
      return productLimit
    }
    // Global limit
    return window.cart_quantity_limit || -1
  }

  /**
   * Split options to `values` (max 3), more (the other more than 3) and all.
   * @param option
   * @returns
   */
  public static splitOption(
    option: ShopifyProductVariantOption,
    autoSelect = false,
  ) {
    const values = option.values.map(value => {
      return {
        value,
        active: false,
        autoSelect,
        available: undefined,
        exists: true,
        hidden: false,
      }
    })

    const transOption: CardProductOption = {
      name: option.name,
      position: option.position,
      autoSelect,
      values: [],
      more: [],
      all: [],
    }

    // Split options to three and more
    if (values.length > 3) {
      const three = values.slice(0, 3)
      const more = values.slice(3)
      transOption.values = three
      transOption.more = more
      transOption.all = [...three, ...more]
    } else {
      transOption.values = values
      transOption.all = transOption.values
    }
    return transOption
  }

  /**
   * The default shopify product json object returns the options as a array of option names, but the json request returns an array of option objects.
   * This method checks if the options array is an array of objects
   * @param product
   * @returns
   */
  public static productHasOptionsWithValues(product: ShopifyProduct) {
    if (!product.options.length) {
      return true
    }
    const exampleOption = product.options[0]
    if (
      exampleOption.hasOwnProperty('name') &&
      exampleOption.hasOwnProperty('values') &&
      exampleOption.hasOwnProperty('position')
    ) {
      return true
    }
    return false
  }

  /**
   * Sort option values: 500 g, 1000 g, 250g -> 250g, 500 g, 1000 g
   * @param option
   * @returns
   */
  public static sortOption(option: ShopifyProductVariantOption) {
    option.values = option.values.sort((a, b) => {
      const aNum = justDigits(a)
      const bNum = justDigits(b)
      if (aNum !== 0 && bNum !== 0) {
        return aNum - bNum
      }

      return a.localeCompare(b)
    })
    return option
  }

  /**
   * Replaces [...] "Default Title" option value
   * @param option
   * @returns
   */
  public static renameOptionValue(optionValue: string) {
    // Remove (...)
    optionValue = optionValue.replace(/\([^)]*\)/g, '')
    // Remove [...]
    optionValue = optionValue.replace(/\[[^\]]*\]/g, '')
    // Trim
    optionValue = optionValue.trim()
    // Remove Default Title
    if (optionValue === 'Default Title') {
      optionValue = ''
    }
    return optionValue
  }

  /**
   * Replaces "Default Title" option values
   * @param option
   * @returns
   */
  public static renameOptionValues(optionValues: string[]) {
    for (let i = 0; i < optionValues.length; i++) {
      optionValues[i] = this.renameOptionValue(optionValues[i])
    }
    return optionValues
  }

  /** Test if two product or variant objects are equal */
  public static equal(
    p1?: ShopifyProduct | ShopifyProductVariant,
    p2?: ShopifyProduct | ShopifyProductVariant,
  ) {
    if (p1 === p2) {
      return true
    }

    if (!p1 || !p2) {
      return p1 === p2
    }

    if (p1.id !== p2.id) {
      return false
    }

    if (p1.available !== p2.available) {
      return false
    }

    if (
      typeof p1.updated_at === 'object' &&
      typeof p2.updated_at === 'object' &&
      p1.updated_at?.getTime() !== p2.updated_at?.getTime()
    ) {
      return false
    } else if (p1.updated_at !== p2.updated_at) {
      return false
    }

    return true
  }
  /**
   * Validates the quantity of a product against an order quantity limit.
   *
   * @param {number} quantity - The initial quantity to validate.
   * @param {number} orderQuantityLimit - The maximum allowed quantity per order. If 0 or negative, no limit is applied.
   * @returns {Object} An object containing the validated quantity and whether the limit was exceeded.
   * @property {number} quantity - The validated quantity, always at least 1 and at most the orderQuantityLimit (if applicable).
   * @property {boolean} exceeded - Indicates whether the initial quantity exceeded the order quantity limit.
   */
  public static validateQuantity(quantity: number, orderQuantityLimit: number) {
    let validatedQuantity = Math.max(1, quantity) // Ensure minimum quantity is 1
    let exceeded = false

    if (orderQuantityLimit > 0) {
      validatedQuantity = Math.min(validatedQuantity, orderQuantityLimit)
      exceeded = quantity > orderQuantityLimit
    }

    return {
      quantity: validatedQuantity,
      exceeded: exceeded,
    }
  }
}
